import React from 'react'
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoney, formatMoneySym } from '@/lib/format'
import { ABBREV_TOOLTIPS } from '@/lib/plain-language'

/** A kept abbreviation (STLY/OTB) that always carries its plain definition on hover. */
function Abbr({ term }: { term: 'STLY' | 'OTB' }) {
  return (
    <abbr title={ABBREV_TOOLTIPS[term]} className="cursor-help no-underline decoration-dotted underline-offset-2 hover:underline">
      {term}
    </abbr>
  )
}

interface PortfolioKpis {
  occupancyPct: number
  occupancyPctStly: number
  adr: number
  adrStly: number
  revpar: number
  revparStly: number
  mtdRevenue: number
  mtdRevenueStly: number
  next30Revenue: number
  next30RevenueStly: number
  currency: string
}

interface PortfolioKpiStripProps {
  kpis: PortfolioKpis
}

function pctDelta(current: number, prior: number): { trend: 'up' | 'down' | 'flat'; label: string } {
  if (prior <= 0) return { trend: 'flat', label: '—' }
  const diff = current - prior
  if (Math.abs(diff) < 0.01) return { trend: 'flat', label: '0.0%' }
  const pct = ((diff / prior) * 100).toFixed(1)
  return { trend: diff > 0 ? 'up' : 'down', label: `${diff > 0 ? '+' : ''}${pct}%` }
}

function YoYBadge({ current, prior }: { current: number; prior: number }) {
  const { trend, label } = pctDelta(current, prior)
  const styles =
    trend === 'up'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : trend === 'down'
      ? 'bg-rose-50 text-rose-700 ring-rose-200'
      : 'bg-slate-100 text-slate-600 ring-slate-200'
  const Icon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset tabular-nums ${styles}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  )
}

// Revenue totals in this strip are rendered at 4xl, so anything four digits or longer is
// deliberately compacted (£1.2k / £31k / £1.2M) rather than spelled out. Below £1,000 —
// and for any negative — there is nothing to compact, so it falls through to the shared
// formatter, which spells the value out in full with thousands separators and no rounding.
function compactCurrency(amount: number, sym: string): string {
  if (amount >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 10_000) return `${sym}${(amount / 1_000).toFixed(0)}k`
  if (amount >= 1_000) return `${sym}${(amount / 1_000).toFixed(1)}k`
  return formatMoneySym(amount, sym)
}

export default function PortfolioKpiStrip({ kpis }: PortfolioKpiStripProps) {
  const sym = currencySymbol(kpis.currency)

  const cells = [
    {
      label: 'Occupancy tonight',
      value: `${kpis.occupancyPct.toFixed(1)}%`,
      delta: <YoYBadge current={kpis.occupancyPct} prior={kpis.occupancyPctStly} />,
      footnote: 'vs same time last year',
    },
    {
      label: 'ADR tonight',
      value: formatMoney(kpis.adr, kpis.currency),
      delta: <YoYBadge current={kpis.adr} prior={kpis.adrStly} />,
      footnote: <>vs <Abbr term="STLY" /></>,
    },
    {
      label: 'RevPAR tonight',
      value: formatMoney(kpis.revpar, kpis.currency),
      delta: <YoYBadge current={kpis.revpar} prior={kpis.revparStly} />,
      footnote: <>vs <Abbr term="STLY" /></>,
    },
    {
      label: 'Month to date',
      value: compactCurrency(kpis.mtdRevenue, sym),
      delta: <YoYBadge current={kpis.mtdRevenue} prior={kpis.mtdRevenueStly} />,
      footnote: <>vs <Abbr term="STLY" /> revenue</>,
    },
    {
      label: <>Next 30 days <Abbr term="OTB" /></>,
      value: compactCurrency(kpis.next30Revenue, sym),
      delta: <YoYBadge current={kpis.next30Revenue} prior={kpis.next30RevenueStly} />,
      footnote: 'on the books',
    },
  ]

  return (
    <div className="border-y border-border/60 bg-white/60 backdrop-blur-sm">
      <div className="grid grid-cols-1 divide-y divide-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-5">
        {cells.map((cell, i) => (
          <div
            key={i}
            className="group relative px-5 py-6 transition-colors hover:bg-muted/40 sm:px-6 sm:py-7"
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {cell.label}
            </div>
            <div className="mt-2.5 flex items-baseline gap-3">
              <span className="text-3xl font-semibold tabular-nums tracking-tight text-foreground sm:text-4xl">
                {cell.value}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              {cell.delta}
              <span className="text-[11px] text-muted-foreground">{cell.footnote}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
